import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { auth, db } from '../../firebase';
import { ContratosState, DEF, Direccion, INST, KEY, Modo, calcular, mergeState } from './contractsCalc';
import s from './css/CalculadoraContratos.module.css';

// Últimos valores usados: en Firestore (un documento por usuario) si hay sesión, si no en localStorage
const COLECCION = 'calculadoraContratos';

const leerLocal = (): ContratosState => {
    try { return mergeState(JSON.parse(localStorage.getItem(KEY) as string)) } catch (e) { return { ...DEF } }
};

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).map(n => s[n as string]).join(' ');

const MODOS: [Modo, string][] = [['risk', 'Tengo $ de riesgo'], ['qty', 'Tengo contratos']];
const DIRECCIONES: [Direccion, string][] = [['long', 'Largo'], ['short', 'Corto']];

const CalculadoraContratos: React.FC = () => {
    const [st, setSt] = useState<ContratosState>(leerLocal);
    const [uid, setUid] = useState<string | null>(null);
    const [cargado, setCargado] = useState(false);
    const guardarTimer = useRef<ReturnType<typeof setTimeout>>();

    // Carga los últimos valores según haya sesión o no
    useEffect(() => {
        return onAuthStateChanged(auth, async (user) => {
            setCargado(false);
            if (user) {
                setUid(user.uid);
                try {
                    const snap = await getDoc(doc(db, COLECCION, user.uid));
                    if (snap.exists()) setSt(mergeState(snap.data()));
                } catch (e) {
                    console.error('Error cargando la calculadora de contratos:', e);
                }
            } else {
                setUid(null);
                setSt(leerLocal());
            }
            setCargado(true);
        });
    }, []);

    // Guarda en cada cambio (en Firestore con un pequeño retardo para no escribir en cada tecla)
    useEffect(() => {
        if (!cargado) return;
        if (!uid) {
            try { localStorage.setItem(KEY, JSON.stringify(st)) } catch (e) { }
            return;
        }
        clearTimeout(guardarTimer.current);
        guardarTimer.current = setTimeout(() => {
            setDoc(doc(db, COLECCION, uid), { ...st, actualizado: serverTimestamp() })
                .catch(e => console.error('Error guardando la calculadora de contratos:', e));
        }, 600);
        return () => clearTimeout(guardarTimer.current);
    }, [st, uid, cargado]);

    const set = <K extends keyof ContratosState>(k: K, v: ContratosState[K]) => setSt(prev => ({ ...prev, [k]: v }));

    // Igual que en la Calculadora Forex: acepta coma o punto como decimal
    const setDecimal = (k: 'entry' | 'sl' | 'tp' | 'risk', value: string) => {
        const normalized = value.replace(',', '.');
        if (/^\d*\.?\d*$/.test(normalized)) set(k, normalized);
    };

    const setContratos = (value: string) => {
        if (/^\d*$/.test(value)) set('qty', value);
    };

    const r = calcular(st);

    return (
        <div className="container-fluid">
            <div className="text-center">
                <h2>Calculadora Contratos</h2>
            </div>

            <div className="container-fluid content pb-3">
                <div className={s.wrap}>
                    <div className={s.seg} role="group" aria-label="Modo">
                        {MODOS.map(([v, label]) => (
                            <button key={v} type="button" aria-pressed={st.mode === v} className={cx(st.mode === v && 'activo')} onClick={() => set('mode', v)}>{label}</button>
                        ))}
                    </div>

                    <div className={s.seg} role="group" aria-label="Dirección">
                        {DIRECCIONES.map(([v, label]) => (
                            <button key={v} type="button" aria-pressed={st.dir === v} className={cx(st.dir === v && 'activo')} onClick={() => set('dir', v)}>{label}</button>
                        ))}
                    </div>

                    <div>
                        <FormControl fullWidth>
                            <InputLabel id="instrumento-label">Instrumento</InputLabel>
                            <Select
                                labelId="instrumento-label"
                                value={r.inst.id}
                                label="Instrumento"
                                onChange={(e: SelectChangeEvent) => set('inst', e.target.value)}
                            >
                                {INST.map(i => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <p className={s.hint}>{r.hint}</p>
                    </div>

                    <div className="row">
                        <div className="col-6 ps-0">
                            <TextField label="Entrada (Precio)" type="text" inputMode="decimal" value={st.entry} onChange={e => setDecimal('entry', e.target.value)} fullWidth />
                        </div>
                        <div className="col-6 pe-0">
                            <TextField label="Stop-Loss (Precio)" type="text" inputMode="decimal" value={st.sl} onChange={e => setDecimal('sl', e.target.value)} fullWidth />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-6 ps-0">
                            <TextField label="Take-Profit (opcional)" type="text" inputMode="decimal" value={st.tp} onChange={e => setDecimal('tp', e.target.value)} fullWidth />
                        </div>
                        <div className="col-6 pe-0">
                            {st.mode === 'risk' ? (
                                <TextField label="Riesgo ($)" type="text" inputMode="decimal" value={st.risk} onChange={e => setDecimal('risk', e.target.value)} fullWidth />
                            ) : (
                                <TextField label="Contratos" type="text" inputMode="numeric" value={st.qty} onChange={e => setContratos(e.target.value)} fullWidth />
                            )}
                        </div>
                    </div>

                    <section className={cx('resultado', r.warn && 'aviso')} aria-live="polite">
                        <span className={s.lbl}>{r.heroLbl}</span>
                        <div className={s.hero}>
                            <span className={s.heroV}>{r.heroV}</span>
                            {r.heroU && <span className={s.heroU}>{r.heroU}</span>}
                        </div>

                        {r.msg && <p className={cx('msg', r.msg.cls)}>{r.msg.text}</p>}

                        {r.rows.length > 0 && (
                            <div className={s.filas}>
                                {r.rows.map(f => (
                                    <div className={s.fila} key={f.k}>
                                        <div>
                                            <div className={s.k}>{f.k}</div>
                                            {f.s && <div className={s.sub}>{f.s}</div>}
                                        </div>
                                        <div className={cx('v', f.cls)}>{f.v}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <div className="text-center mt-3">
                        <Link to="/calculadora-forex" className={s.volver}>← Volver a Calculadora Forex</Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CalculadoraContratos;
